import { ref, onBeforeUnmount, getCurrentInstance, computed } from 'vue';
import getCurrentDateInSeconds from '@/utilities/getCurrentDateInSeconds';

export default function useManageCardMembers() {
  const _this = getCurrentInstance()?.proxy as any;
  if (!_this) return;

  const manageMembersTimeout = ref(null) as any;
  const pendingCallback = ref<(() => void) | null>(null);

  const currentUserId = computed((): string => {
    return _this?.$store.getters.getUserId;
  });

  onBeforeUnmount(() => {
    // If there's a pending callback, execute it immediately
    if (pendingCallback.value) {
      pendingCallback.value();
      pendingCallback.value = null;
    }

    if (manageMembersTimeout.value) {
      clearTimeout(manageMembersTimeout.value);
      manageMembersTimeout.value = null;
    }
  });

  const handleToggleMember = ({
    cardId,
    user_id = currentUserId.value,
  }: {
    cardId: string;
    user_id: string;
  }) => {
    if (!cardId || !user_id) return;
    const card = _this.$store.getters.getCards[cardId] ?? {};
    const currentMembers = [...(card.members || [])];
    const member = currentMembers.find((member) => member.user_id === user_id);
    if (member) {
      unassignMember(cardId, member, user_id);
    } else {
      assignMember(cardId, user_id);
    }
  };

  const manageMemberTimeout = (callback: () => void, delay: number = 300) => {
    if (manageMembersTimeout.value) {
      clearTimeout(manageMembersTimeout.value);
    }
    // Store the callback for potential immediate execution on unmount
    pendingCallback.value = callback;

    if (delay === 0) {
      callback();
      pendingCallback.value = null;
      return;
    }
    manageMembersTimeout.value = setTimeout(() => {
      callback();
      pendingCallback.value = null;
    }, delay);
  };

  const assignMember = (
    cardId: string,
    user_id: string = currentUserId.value,
    delay: number | undefined = undefined
  ) => {
    if (!cardId || !user_id) return;
    const card = _this.$store.getters.getCards[cardId] ?? {};
    const currentMembers = [...(card.members || [])];

    const memberToAdd = {
      user_id,
      assigned_date: getCurrentDateInSeconds(),
    };

    const payload = {
      cardId,
      memberToAdd,
    };

    _this.$store.dispatch('localAddMemberToCard', payload);

    manageMemberTimeout(() => {
      _this.$store.dispatch('addMemberToCard', payload).catch((err: any) => {
        if (err.response?.status === 400 && err.response?.data?.message === 'member already exists')
          return;
        _this.$store.dispatch('localUpdateCardField', {
          cardId,
          fieldName: 'members',
          fieldValue: currentMembers,
        });
      });
    }, delay);
  };

  const unassignMember = (
    cardId: string,
    member: any,
    user_id: string = currentUserId.value,
    delay: number | undefined = undefined
  ) => {
    if (!cardId || !member || !user_id) return;
    const memberToRemove = {
      ...member,
      user_id,
    };
    const payload = {
      cardId,
      memberToRemove,
    };
    _this.$store.dispatch('localRemoveMemberFromCard', payload);

    manageMemberTimeout(() => {
      _this.$store.dispatch('removeMemberFromCard', payload).catch((err: any) => {
        if (err.response?.status === 404 && err.response?.data?.message === 'member not found')
          return;
        _this.$store.dispatch('localAddMemberToCard', {
          cardId,
          memberToAdd: memberToRemove,
        });
      });
    }, delay);
  };

  return {
    handleToggleMember,
    assignMember,
    unassignMember,
  };
}
